import { ColumnDef, createColumnHelper } from '@tanstack/react-table'
import numeral from 'numeral'
import { useMemo, useEffect } from 'react'
import { MdChevronRight } from 'react-icons/md'

import { useSort } from 'src/providers/SortProvider'

import { IconButton } from 'src/components/atoms/IconButton'
import { Image } from 'src/components/atoms/Image'
import { InternalLink } from 'src/components/atoms/Link'
import { LoadingSpinner } from 'src/components/atoms/LoadingSpinner'
import { Pagination } from 'src/components/molecules/Pagination'
import { SearchControls } from 'src/components/molecules/SearchControls'
import { Table } from 'src/components/molecules/Table'

import { useGetFunderViewLazyQuery } from 'src/config/generated/graphql'
import { viewProfilePath } from 'src/config/paths'
import { env } from 'src/env/client.mjs'
import { usePagination } from 'src/hooks/usePagination'
import { useSearch } from 'src/hooks/useSearch'
import { FunderType } from 'src/types/funder'

import { buildLeaderboardWhere } from './helper'

export const FundersTab = () => {
  const [getFunderView, { data: funderData, loading: funderLoading }] =
    useGetFunderViewLazyQuery()

  const totalCount = useMemo(() => {
    return funderData?.FunderView_aggregate?.aggregate?.count || 0
  }, [funderData?.FunderView_aggregate])

  const { currentPage, limit, offset, onPageClick } = usePagination()

  const { searchQuery, setSearchQuery, clearSearchQuery } = useSearch()

  const { sort } = useSort()

  useEffect(() => {
    const where = buildLeaderboardWhere({
      searchQuery: searchQuery as string
    })
    const orderBy = { [sort.sortColumn!]: sort.sortOrder }

    getFunderView({ variables: { limit, offset, where, order_by: orderBy } })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limit, offset, searchQuery, sort.sortColumn, sort.sortOrder])

  const columnHelper = createColumnHelper<FunderType>()
  const columns: ColumnDef<FunderType, any>[] = [
    columnHelper.accessor('Rank', {
      header: 'Overall Ranking',
      meta: { align: 'center' },
      cell: props => (
        <div className='flex text-white bg-tomThumb items-center justify-center text-2xl max-w-40 max-h-20 p-4 font-body rounded-2xl'>
          <p>{numeral(props.getValue()).format('0,0')}</p>
        </div>
      )
    }),
    columnHelper.accessor('Name', {
      header: 'Name',
      meta: { align: 'center' },
      cell: props => (
        <InternalLink
          openInNewTab
          className='flex flex-col items-center'
          href={viewProfilePath({
            routeParams: { uri: props.row.original.Uri as string }
          })}
        >
          <span className='text-center'>{props.getValue()}</span>
          <Image
            alt={`Avatar for ${props.getValue()}`}
            width='80'
            height='80'
            src={`${env.NEXT_PUBLIC_ASSETS_DOMAIN}/${props.row.original.Avatar}`}
          />
        </InternalLink>
      )
    }),
    columnHelper.accessor('TotalCount', {
      header: 'All-time Count',
      meta: { align: 'center' },
      cell: props => numeral(props.getValue()).format('0,0')
    }),
    columnHelper.accessor('CurrentSeason', {
      header: 'Current Season',
      meta: { align: 'center' },
      cell: props => numeral(props.getValue()).format('0,0')
    }),
    columnHelper.accessor('PlanterCount', {
      header: 'Planters Supported',
      meta: { align: 'center' },
      cell: props => numeral(props.getValue()).format('0,0')
    }),
    columnHelper.accessor('Uri', {
      header: '',
      meta: { align: 'right' },
      size: 90,
      cell: props => (
        <InternalLink
          href={viewProfilePath({
            routeParams: { uri: props.row.original.Uri as string }
          })}
        >
          <IconButton
            icon={MdChevronRight}
            ariaLabel=''
            color='primary-inverted'
            size='large'
          />
        </InternalLink>
      )
    })
  ]

  return (
    <div className='flex flex-col'>
      <div className='w-full md:w-96 self-end'>
        <SearchControls
          currentSearchQuery={searchQuery as string}
          onSearch={setSearchQuery}
          onClear={clearSearchQuery}
          showOnClear={false}
        />
      </div>
      <LoadingSpinner isLoading={funderLoading}>
        <Table columns={columns} data={funderData?.FunderView || []} />
        <Pagination
          count={totalCount}
          onChange={(page: number) => onPageClick(page)}
          page={currentPage}
          pageSize={limit}
        />
      </LoadingSpinner>
    </div>
  )
}
